/**
 * OneSignal Registration Service - SDK-Only Architecture
 * 
 * Servizio semplificato che delega TUTTO all'SDK OneSignal v16+.
 * Non effettua chiamate API dirette - solo localStorage e database sync.
 */

import { supabase } from '@/lib/supabase/client';
import { debugLog, debugError, debugWarn } from '@/utils/debugLogger';
import { safeLocalStorage } from '@/utils/safeStorage';

class OneSignalRegistrationService {
  
  /**
   * Recupera il Player ID dall'SDK OneSignal o da localStorage
   */
  async getPlayerId(): Promise<string | null> {
    try {
      // Prima prova dall'SDK v16+
      if (typeof window !== 'undefined') {
        const OneSignal = (window as any).OneSignal;
        if (OneSignal?.User?.PushSubscription?.id) {
          const sdkPlayerId = OneSignal.User.PushSubscription.id;
          if (sdkPlayerId) {
            debugLog('notifications', 'Player ID from SDK:', sdkPlayerId, 'OneSignalRegistrationService');
            safeLocalStorage.setItem('onesignal_player_id', sdkPlayerId);
            return sdkPlayerId;
          }
        }
      }

      // Fallback a localStorage
      const storedId = safeLocalStorage.getItem('onesignal_player_id');
      if (storedId) {
        debugLog('notifications', 'Player ID from localStorage:', storedId, 'OneSignalRegistrationService');
        return storedId;
      }

      debugLog('notifications', 'No Player ID found', undefined, 'OneSignalRegistrationService');
      return null;
    } catch (error) {
      debugError('notifications', 'Error getting Player ID:', error, 'OneSignalRegistrationService');
      return safeLocalStorage.getItem('onesignal_player_id');
    }
  }

  /**
   * Salva il Player ID su localStorage e database
   */
  async savePlayerId(playerId: string): Promise<void> {
    if (!playerId) {
      debugWarn('notifications', 'Cannot save empty Player ID', undefined, 'OneSignalRegistrationService');
      return;
    }

    debugLog('notifications', 'Saving Player ID:', playerId, 'OneSignalRegistrationService');
    safeLocalStorage.setItem('onesignal_player_id', playerId);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        const { error } = await supabase
          .from('user_preferences')
          .upsert({
            user_id: user.id,
            onesignal_player_id: playerId,
            push_notifications_enabled: true,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'user_id'
          });

        if (error) {
          debugError('notifications', 'Failed to save Player ID to database:', error, 'OneSignalRegistrationService');
        } else {
          debugLog('notifications', 'Player ID saved to database', undefined, 'OneSignalRegistrationService');
        }
      }
    } catch (error) {
      debugError('notifications', 'Error saving Player ID to database:', error, 'OneSignalRegistrationService');
    }
  }

  /**
   * Gestisce il login utente - chiama OneSignal.login() tramite SDK
   */
  async handleUserLogin(userId: string): Promise<void> {
    if (!userId) {
      debugWarn('notifications', 'Cannot login without userId', undefined, 'OneSignalRegistrationService');
      return;
    }

    debugLog('notifications', 'Handling user login for:', userId, 'OneSignalRegistrationService');

    try {
      const OneSignal = (window as any).OneSignal;
      if (OneSignal?.login) {
        await OneSignal.login(userId);
        debugLog('notifications', 'OneSignal.login() called successfully', undefined, 'OneSignalRegistrationService');
      }

      const playerId = await this.getPlayerId();
      if (playerId) {
        await this.savePlayerId(playerId);
      }
    } catch (error) {
      debugError('notifications', 'Error during user login:', error, 'OneSignalRegistrationService');
    }
  }

  /**
   * Gestisce il logout utente - chiama OneSignal.logout() tramite SDK
   */
  async handleUserLogout(): Promise<void> {
    debugLog('notifications', 'Handling user logout', undefined, 'OneSignalRegistrationService');

    try {
      const OneSignal = (window as any).OneSignal;
      if (OneSignal?.logout) {
        await OneSignal.logout();
        debugLog('notifications', 'OneSignal.logout() called successfully', undefined, 'OneSignalRegistrationService');
      }
    } catch (error) {
      debugError('notifications', 'Error during user logout:', error, 'OneSignalRegistrationService');
    }
  }

  /**
   * Verifica se l'utente è registrato (ha un Player ID valido)
   */
  async isUserRegistered(): Promise<boolean> {
    const playerId = await this.getPlayerId();
    return !!playerId;
  }

  /**
   * Alias per getPlayerId per retrocompatibilità
   */
  async getCurrentPlayerId(): Promise<string | null> {
    return this.getPlayerId();
  }

  /**
   * Richiede i permessi di notifica
   */
  async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      throw new Error('Browser does not support notifications');
    }

    if (Notification.permission === 'granted' || Notification.permission === 'denied') {
      return Notification.permission;
    }

    return Notification.requestPermission();
  }

  /**
   * Registra l'utente per le notifiche tramite SDK
   */
  async registerUser(): Promise<{ success: boolean; playerId?: string; error?: string }> {
    try {
      const permission = await this.requestNotificationPermission();
      
      if (permission !== 'granted') {
        return { success: false, error: 'Permission denied' };
      }

      // L'SDK gestisce la registrazione automaticamente
      const OneSignal = (window as any).OneSignal;
      if (OneSignal?.Notifications?.requestPermission) {
        await OneSignal.Notifications.requestPermission();
      }

      // Attendi un momento per la sincronizzazione SDK
      await new Promise(resolve => setTimeout(resolve, 1000));

      const playerId = await this.getPlayerId();
      if (playerId) {
        await this.savePlayerId(playerId);
        return { success: true, playerId };
      }

      return { success: false, error: 'No player ID generated' };
    } catch (error) {
      debugError('notifications', 'Registration error:', error, 'OneSignalRegistrationService');
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Disregistra l'utente dalle notifiche
   */
  async unregisterUser(): Promise<boolean> {
    try {
      const OneSignal = (window as any).OneSignal;
      if (OneSignal?.User?.PushSubscription?.optOut) {
        await OneSignal.User.PushSubscription.optOut();
      }
      
      safeLocalStorage.removeItem('onesignal_player_id');
      return true;
    } catch (error) {
      debugError('notifications', 'Unregister error:', error, 'OneSignalRegistrationService');
      return false;
    }
  }

  /**
   * Verifica se le notifiche sono supportate
   */
  isSupported(): boolean {
    return typeof window !== 'undefined' && 
           'Notification' in window && 
           'serviceWorker' in navigator;
  }

  /**
   * Ottiene lo stato corrente dei permessi
   */
  getPermissionStatus(): NotificationPermission | 'unsupported' {
    if (!this.isSupported()) {
      return 'unsupported';
    }
    return Notification.permission;
  }
}

export const oneSignalRegistrationService = new OneSignalRegistrationService();
